import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Alert, Spinner, Card } from "react-bootstrap";
import api from "../../api";
import { AuthContext } from "../../context/AuthContext";
import DynamicFormRenderer from "../../components/forms/DynamicFormRenderer";
import FormScoreSummary from "../../components/forms/FormScoreSummary";
import FormSectionScoreBreakdown from "../../components/forms/FormSectionScoreBreakdown";
import FormFieldScoreBreakdown from "../../components/forms/FormFieldScoreBreakdown";
import FormReviewTimeline from "../../components/forms/FormReviewTimeline";
import FormTestExecutionPanel from "../../components/forms/FormTestExecutionPanel";
import StatusChip from "../../components/common/StatusChip";
import { viewAuthenticatedFileInNewTab, getFileAccessErrorMessage } from "../../utils/fileAccess";
import { toast } from "react-toastify";
import "./FormSubmissionViewPage.css";

// Phase 3B, §26 (read-only response view) + Phase 3C, §27 (Result section).
// Score/section/field breakdown and review timeline are fetched
// independently from the response runtime and rendered ONLY when the
// backend actually returns something — a non-scored form's submission
// simply shows no Result card at all, and each sub-fetch tolerates its own
// 403/404 (e.g. a breakdown withheld pre-finalization for the owner)
// without blocking the rest of the page. Every number is display-only,
// straight from the backend — nothing here is recalculated in the browser.
function useRolePrefix() {
  const path = window.location.pathname;
  const match = path.match(/^\/([^/]+)\//);
  return match ? match[1] : "admin";
}

export default function FormSubmissionViewPage() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const rolePrefix = useRolePrefix();
  const { user } = useContext(AuthContext) || {};

  const [runtime, setRuntime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [result, setResult] = useState(null);
  const [sectionScores, setSectionScores] = useState(null);
  const [fieldScores, setFieldScores] = useState(null);
  const [reviewStages, setReviewStages] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await api.get(`/form-submissions/${submissionId}/runtime`);
        setRuntime(res.data?.data);
      } catch (err) {
        console.error("Fetch submission runtime error:", err);
        setLoadError(err.response?.data?.message || "Failed to load this submission.");
      } finally {
        setLoading(false);
      }
    })();

    // Independent of the runtime fetch — a non-scored form, or a result
    // withheld pre-finalization, is a normal outcome here, not a page error.
    api.get(`/form-submissions/${submissionId}/result`).then((res) => setResult(res.data?.data)).catch(() => setResult(null));
    api.get(`/form-submissions/${submissionId}/result/sections`).then((res) => setSectionScores(res.data?.data)).catch(() => setSectionScores(null));
    api.get(`/form-submissions/${submissionId}/result/fields`).then((res) => setFieldScores(res.data?.data)).catch(() => setFieldScores(null));
    api.get(`/form-submissions/${submissionId}/review-stages`).then((res) => setReviewStages(res.data?.data)).catch(() => setReviewStages(null));
  }, [submissionId]);

  const handleViewFile = async (file) => {
    try {
      await viewAuthenticatedFileInNewTab(`/form-submissions/${submissionId}/files/${file.id}/view`);
    } catch (err) {
      toast.error(getFileAccessErrorMessage(err));
    }
  };

  if (loading) {
    return <div className="unit-page fsv-theme"><div className="unit-loader"><Spinner animation="border" /><p>Loading submission...</p></div></div>;
  }
  if (loadError || !runtime) {
    return (
      <div className="unit-page fsv-theme">
        <Alert variant="danger" className="fsv-alert">
          <i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
          <span>{loadError || "This submission could not be loaded."}</span>
        </Alert>
        <Button className="unit-btn unit-btn-light fsv-back-btn" onClick={() => navigate(`/${rolePrefix}/my-assigned-forms`)}>
          <i className="fa-solid fa-arrow-left" aria-hidden="true"></i>
          Back to My Assigned Forms
        </Button>
      </div>
    );
  }

  return (
    <div className="unit-page fsv-theme">

      <div className="unit-header fsv-header">
        <div>
          <div className="unit-page-label">{runtime.form.form_type_name}</div>
          <h2>{runtime.form.name}</h2>
          {runtime.assignment.title && <p className="fsv-subtitle">{runtime.assignment.title}</p>}
        </div>
        <div className="unit-header-actions">
          {/* Dynamic Forms visibility audit, §11: a reopened ("Changes
              Requested") submission is editable again -- runtime.submission
              .is_editable already reflects this (status DRAFT/REOPENED).
              Routes into the SAME existing fill flow/submission id, never a
              second response. Shown only to the submission's own assignee --
              a reviewer/assigner viewing the same response is not the one
              who can correct it, and the fill route's own access check would
              otherwise just dead-end them with a 403. */}
          {runtime.submission.is_editable
            && runtime.submission.form_assignment_user_id
            && user?.id != null
            && Number(runtime.submission.assignee_user_id) === Number(user.id) && (
            <Button
              className="unit-btn unit-btn-primary fsv-correct-btn"
              onClick={() => navigate(`/${rolePrefix}/my-assigned-forms/${runtime.submission.form_assignment_user_id}/fill/${submissionId}`)}
            >
              <i className="fa-solid fa-pen-to-square" aria-hidden="true"></i>
              Correct Response
            </Button>
          )}
          <Button className="unit-btn unit-btn-light fsv-back-btn" onClick={() => navigate(`/${rolePrefix}/my-assigned-forms`)}>
            <i className="fa-solid fa-arrow-left" aria-hidden="true"></i>
            Back
          </Button>
        </div>
      </div>

      <div className="fsv-card fsv-summary-card">
        <div className="fsv-summary-grid">
          {/* Public Form Respondent Identity, §10: SUBJECT (who this
              response is ABOUT, e.g. "Team Member Being Rated") and
              RESPONDENT (submitted_by, who actually filled it in) shown as
              two separate items — never merged, so "Ratana Dubey was
              reviewed by Parul Sharma" is always readable at a glance.
              Subject only renders for forms that configure a subject field
              (most forms don't) — see services/formRuntimeService.js. */}
          {runtime.subject && (
            <div className="fsv-summary-item">
              <span className="fsv-summary-label"><i className="fa-solid fa-user-check" aria-hidden="true"></i>Reviewed Employee</span>
              <strong className="fsv-summary-value">{runtime.subject.name}</strong>
            </div>
          )}
          {runtime.submission.respondent_name && (
            <div className="fsv-summary-item">
              <span className="fsv-summary-label"><i className="fa-solid fa-pen" aria-hidden="true"></i>{runtime.subject ? "Reviewed By" : "Submitted By"}</span>
              <strong className="fsv-summary-value">{runtime.submission.respondent_name}</strong>
              {runtime.submission.respondent_email && <span className="fsv-summary-sub">{runtime.submission.respondent_email}</span>}
            </div>
          )}
          {runtime.submission.submission_channel && (
            <div className="fsv-summary-item">
              <span className="fsv-summary-label"><i className="fa-solid fa-link" aria-hidden="true"></i>Submission Method</span>
              <strong className="fsv-summary-value">{runtime.submission.submission_channel === "PUBLIC_LINK" ? "Public Link" : "Portal"}</strong>
            </div>
          )}
          <div className="fsv-summary-item">
            <span className="fsv-summary-label"><i className="fa-solid fa-code-branch" aria-hidden="true"></i>Version</span>
            <strong className="fsv-summary-value">{runtime.form.version_number}</strong>
          </div>
          <div className="fsv-summary-item">
            <span className="fsv-summary-label"><i className="fa-solid fa-calendar-check" aria-hidden="true"></i>Submitted</span>
            <strong className="fsv-summary-value">{runtime.submission.submitted_at ? new Date(runtime.submission.submitted_at).toLocaleString() : "—"}</strong>
          </div>
          {runtime.assignment.project_name && (
            <div className="fsv-summary-item">
              <span className="fsv-summary-label"><i className="fa-solid fa-diagram-project" aria-hidden="true"></i>Project / Module</span>
              <strong className="fsv-summary-value">{runtime.assignment.project_name}{runtime.assignment.project_module_name ? ` / ${runtime.assignment.project_module_name}` : ""}</strong>
            </div>
          )}
          {runtime.assignment.related_task_title && (
            <div className="fsv-summary-item">
              <span className="fsv-summary-label"><i className="fa-solid fa-list-check" aria-hidden="true"></i>Task</span>
              <strong className="fsv-summary-value">{runtime.assignment.related_task_title}</strong>
            </div>
          )}
          <div className="fsv-summary-item fsv-summary-item-status">
            <span className="fsv-summary-label"><i className="fa-solid fa-flag" aria-hidden="true"></i>Status</span>
            <StatusChip status={runtime.submission.status} />
          </div>
        </div>
      </div>

      {result?.available && (
        <>
          <FormScoreSummary result={result} />
          {(sectionScores?.length > 0 || fieldScores?.length > 0) && (
            <Card className="mb-3">
              <Card.Body>
                {sectionScores?.length > 0 && (
                  <>
                    <strong className="fsv-card-title mb-2"><i className="fa-solid fa-layer-group" aria-hidden="true"></i>Section Breakdown</strong>
                    <FormSectionScoreBreakdown sections={sectionScores} />
                  </>
                )}
                {fieldScores?.length > 0 && (
                  <>
                    <strong className="fsv-card-title mt-3 mb-2"><i className="fa-solid fa-table-list" aria-hidden="true"></i>Field Breakdown</strong>
                    <FormFieldScoreBreakdown fields={fieldScores} />
                  </>
                )}
              </Card.Body>
            </Card>
          )}
        </>
      )}

      <FormTestExecutionPanel submissionId={submissionId} />

      {reviewStages?.length > 0 && (
        <Card className="mb-3">
          <Card.Body>
            <strong className="fsv-card-title mb-2"><i className="fa-solid fa-timeline" aria-hidden="true"></i>Review / Approval History</strong>
            <FormReviewTimeline stages={reviewStages} />
          </Card.Body>
        </Card>
      )}

      <DynamicFormRenderer
        mode="fill"
        sections={runtime.structure.sections}
        values={runtime.valuesByFieldKey}
        repeatingRowsBySection={runtime.repeatingRowsBySection}
        filesByFieldKey={runtime.filesByFieldKey}
        filesByRow={runtime.filesByRow}
        onViewFile={handleViewFile}
        executionProfile={runtime.execution_profile}
        readOnly
      />
    </div>
  );
}
